import * as React from "react";
import { useJsApiLoader, GoogleMap, Marker } from "@react-google-maps/api";
import { useResource, useWindowSize } from "hooks";
import { Condition } from "components/elements";
import { config } from "utils";
import { ORDER_TYPES } from "utils/constants";
import { combineHours, displayedHoursOverride } from "utils/helpers/time";
import { determineBreakPoint } from "utils/helpers/other";
import LocationMarker from "./LocationMarker";

const { Condition: If } = Condition;
const { DELIVERY } = ORDER_TYPES;

// map style
const isUsingMapStyle = config.theme.location.map_style;

// use marker
const userMarker = config?.images?.indicator_pin_delivery;

const mapOption = isUsingMapStyle
  ? // eslint-disable-next-line import/no-unresolved
    { disableDefaultUI: true, styles: require("GeoConfig") }
  : {};

// default position
const defaultCenter = config.theme.location.map_geo;

// map height
const containerStyle = {
  height: "100%",
};

// using @react-google-maps/api: please do not change the order of functions
const LocationMap = ({
  isMarketPlace,
  type,
  googleMapKey,
  history,
  diningOption,
  ...props
}) => {
  const googleMapRef = React.useRef(null);
  const { width: windowWidth } = useWindowSize({ debounce: 200 });
  const { name: breakPoint } = determineBreakPoint(windowWidth);
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: googleMapKey,
  });
  const [openLocation, setOpenLocation] = React.useState(null);
  const [mapCenter, setMapCenter] = React.useState(
    props.center.lat && props.center.lng
      ? props.center
      : { lat: defaultCenter.lat, lng: defaultCenter.long },
  );

  const [{ resource, fetching }] = useResource({
    method: "post",
    path: "/locations",
    data: {
      diningOption,
    },
  });

  /**
   * When props.center is updated, sync the local state
   * to the parent state. This allows the map to update
   * when the user selects an address, which is managed
   * in `withLocation.jsx`.
   *
   * FIXME: This behavior indicates a multiple-sources-of-truth
   * problem. Instead of having multiple states that we have to
   * sync via effects, this component should simply update the
   * remote state when a user selects a marker. Using an effect
   * here is just a quick workaround.
   */
  React.useEffect(() => {
    if (props.center.lat && props.center.lng) {
      setMapCenter(props.center);
    }
  }, [props.center.lat, props.center.lng]);

  React.useEffect(() => {
    if (openLocation && props.locations.find((i) => i.id === openLocation)) {
      props.onClickMarker(openLocation);
    }
  }, [openLocation]);

  if (loadError) return "Error loading";
  if (!isLoaded) return "Loading Maps...";

  const switchLocationsData = () => {
    if (diningOption === DELIVERY) {
      return resource;
    }
    return props.locations;
  };

  // Organize locations by :hours,delivery,dineIn,pickup
  const locations = () => {
    const locationsData = switchLocationsData();
    const dayOfWeek = new Date().getDay();
    return locationsData.map((i) => {
      let {
        delivery = [],
        kiosk = [],
        pickup = [],
      } = combineHours(i.hours || []);

      delivery = displayedHoursOverride(delivery, config.isOlo);
      kiosk = displayedHoursOverride(kiosk, config.isOlo);
      pickup = displayedHoursOverride(pickup, config.isOlo);

      return {
        ...i,
        hours: {
          todayDelivery: delivery.find((x) => x.days.includes(dayOfWeek)) || {},
          todayDineIn: kiosk.find((x) => x.days.includes(dayOfWeek)) || {},
          todayPickup: pickup.find((x) => x.days.includes(dayOfWeek)) || {},
        },
      };
    });
  };

  const allLocations = locations();

  /**
  @function onMarkerSelect - Zooms and centers map on selected location. If viewport is md or lower, pans google map cam so that selected location is visible.
  @param {string} id - the id for the selected location to open the info for ex:"6074e3b04a30f076eef1f631"
  @param {object} coords - coordinates for the selected location
  @param {number} coords.lat - location lattitude
  @param {number} coords.long -  location longitude
  */
  const onMarkerSelect = (id, { lat, long }) => {
    const shouldOffsetMapCam =
      breakPoint === "md" || breakPoint === "sm" || breakPoint === "xs";
    setOpenLocation(id);
    setMapCenter({ lat, lng: long });
    if (googleMapRef.current) googleMapRef.current.setZoom(11);
    if (shouldOffsetMapCam) {
      googleMapRef.current.panBy(-300, 0);
    }
  };

  const onClose = () => setOpenLocation(null);

  // display all location with their marker
  const displayLocation = () => {
    return allLocations
      .filter(
        (oneLocation) =>
          oneLocation?.address?.geo?.lat && oneLocation?.address?.geo?.long,
      )
      .map((location) => {
        const locationId = isMarketPlace
          ? location.locations[0].restaurantGroup
          : location.id;
        return (
          <LocationMarker
            key={locationId}
            id={locationId}
            type={type}
            onClick={() => onMarkerSelect(location.id, location.address.geo)}
            onClose={() => onClose}
            isSelectedLocation={openLocation === location.id}
            name={location.name}
            isDeliveryEnabled={location.isDeliveryEnabled}
            isPickupEnabled={location.isPickupEnabled}
            address={location.address}
            hours={location.hours}
            slug={location.slug}
            restaurantGroup={location.restaurantGroup}
            isOpen={location.isOpen}
            isPreorderRequired={location.isPreorderRequired}
            history={history}
            diningOption={diningOption}
            isMarketPlace={isMarketPlace}
          />
        );
      });
  };

  // store zoom and center in state, an on map click change that info...
  return (
    <GoogleMap
      options={mapOption}
      zoom={+config.theme.location.map_zoom || (props.center.lat ? 13 : 12)}
      center={mapCenter}
      mapContainerStyle={containerStyle}
      onLoad={(map) => {
        googleMapRef.current = map;
      }}
    >
      <If is={!fetching}>{displayLocation()}</If>
      <If is={props.center.lat && props.center.lng}>
        <Marker
          position={props.center}
          icon={{
            scaledSize: new window.google.maps.Size(31, 43),
            url: userMarker,
          }}
        />
      </If>
    </GoogleMap>
  );
};

export default LocationMap;
